/**
 * Wall — the Phase-1 category. The `path` instance param (Path2D, open) stores the wall's
 * REFERENCE line — which line that is, is named by `locationLine` (2.6a, docs/bim-plan/06
 * Part 4; default wallCenterline preserves pre-2.6 behavior exactly). Plan thickness comes
 * from the type's assembly; vertical extent = level elevation + baseOffsetFt … +
 * unconnectedHeightFt. `flipped` mirrors the body about the stored path (exterior = LEFT
 * of draw direction unflipped; layers[0] = exterior-most).
 */
import type { CategorySchema } from '../../registry'
import { COPING_TYPE_GROUP, COPING_INSTANCE_GROUP } from '../../coping'
import { TEXTURE_PLACEMENT_GROUP } from '../../texturePlacement'
import { INSULATION_PARAM_GROUP } from '../../insulationSpec'
import { DEFAULT_LOCATION_LINE, LOCATION_LINES } from '../../locationLine'
import { STUD_SPACINGS } from '../../wallFraming'
import { METAL_STUD_SIZES, WALL_CORE_TYPES, WOOD_STUD_SIZES } from '../../wallCore'

export const wallSchema: CategorySchema = {
  id: 'wall',
  pickRank: 1,
  pathParamKey: 'path',
  label: 'Wall',
  usesAssembly: 'wall',
  typeParams: {
    groups: [
      {
        label: 'Construction',
        params: [
          {
            key: 'function', label: 'Function', kind: 'choice', default: 'exterior',
            choices: [
              { value: 'exterior', label: 'Exterior' },
              { value: 'interior', label: 'Interior' },
            ],
          },
          {
            // WALL CORE DEFINITION round (2026-08-18, Matt): the type declares WHAT
            // its structural core is; commands/library.ts's conform-on-write derives
            // the core LAYER's material (the canonical family material — wallCore.ts)
            // and, for the stud families, its thickness from the selected size
            // (actual widths, never nominal). CMU/Concrete keep a manually-entered
            // core width — their size IS the layer thickness.
            key: 'coreType', label: 'Core', kind: 'choice', default: 'wood',
            choices: WALL_CORE_TYPES.map((t) => ({ value: t.value, label: t.label })),
          },
          {
            key: 'woodStudSize', label: 'Stud Size', kind: 'choice', default: '2x6',
            conditionalOn: { param: 'coreType', equals: 'wood' },
            choices: WOOD_STUD_SIZES.map((s) => ({ value: s.value, label: `${s.label} — ${s.actualLabel} actual` })),
          },
          {
            key: 'metalStudSize', label: 'Stud Size', kind: 'choice', default: '3-5/8',
            conditionalOn: { param: 'coreType', equals: 'metal' },
            choices: METAL_STUD_SIZES.map((s) => ({ value: s.value, label: s.label })),
          },
        ],
      },
      {
        // WALL ASSEMBLY SCHEDULE round (2026-08-18, Matt): fire rating is a property
        // of the CURATED TYPE, never a per-wall toggle. A rating is only true for a
        // SPECIFIC tested/listed assembly (an exact layer stack, thickness, fastening
        // schedule matching a real UL/GA design) — a generic on/off switch on any wall
        // would let someone assert a rating for a build-up that was never tested to it
        // (the wall-core round's own trust model applies here too: the type declares
        // what it structurally IS, curated by whoever sets it up, not inferred or
        // freely toggled). A firm-standard "1-Hr — 2×4 — GWB Both Sides" is its own
        // type, distinct from the otherwise-identical unrated type.
        label: 'Fire Rating',
        params: [
          {
            key: 'fireRating', label: 'Fire Rating', kind: 'choice', default: 'none',
            choices: [
              { value: 'none', label: 'None' },
              { value: '1hr', label: '1-Hour' },
              { value: '2hr', label: '2-Hour' },
              { value: '3hr', label: '3-Hour' },
              { value: '4hr', label: '4-Hour' },
            ],
          },
          {
            key: 'ulListing', label: 'UL / GA Listing', kind: 'text', default: '',
            conditionalOn: { param: 'fireRating', equals: ['1hr', '2hr', '3hr', '4hr'] },
          },
        ],
      },
      {
        // Free-text schedule fields — same "curated text, not a picklist yet" call as
        // the door/window Schedule Data groups. Insulation R-VALUE is the assembly's
        // documented SPEC (what a batt-insulated instance of this type is built with),
        // deliberately decoupled from the existing per-instance `insulation` toggle
        // (Framing group, above) — that toggle only controls the plan/section batt
        // HATCH, a drawing-representation concern; the schedule documents design
        // intent for the type regardless of which instances happen to have the hatch
        // switched on for a given drawing.
        label: 'Schedule Data',
        params: [
          // superseded by the Insulation group (insulation.ts) — kept readable as the schedule's fallback
          { key: 'insulationRValue', label: 'Insulation R-Value', kind: 'text', default: '', hidden: true },
          { key: 'remarks', label: 'Remarks', kind: 'text', default: '' },
        ],
      },
      // INSULATION SCHEDULE round (2026-10-03): the type's insulation spec — feeds the
      // derived, fit-checked insulation schedule on the General Notes sheet (insulation.ts)
      INSULATION_PARAM_GROUP,
      // WALL COPINGS (2026-10-06): the type's default cap for exposed tops — coping.ts
      COPING_TYPE_GROUP,
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Constraints',
        params: [
          { key: 'path', label: 'Path', kind: 'path2d', default: null },
          {
            key: 'locationLine', label: 'Location Line', kind: 'choice',
            default: DEFAULT_LOCATION_LINE,
            choices: LOCATION_LINES.map((l) => ({ value: l.value, label: l.label })),
          },
          { key: 'baseOffsetFt', label: 'Base Offset', kind: 'length', default: 0, min: -20, max: 20 },
          {
            // TOP-LEVEL round (2026-08-10, Matt's Revit-parity ask): a wall top may
            // ASSOCIATE with a level — it flexes when the level moves — or stay a
            // plain unconnected height. The column topMode pattern verbatim.
            key: 'topMode', label: 'Top', kind: 'choice', default: 'height',
            choices: [
              { value: 'height', label: 'Unconnected height' },
              { value: 'level', label: 'Up to level' },
            ],
          },
          {
            key: 'unconnectedHeightFt', label: 'Unconnected Height', kind: 'length', default: 9, min: 0.5, max: 60,
            conditionalOn: { param: 'topMode', equals: 'height' },
          },
          {
            // '' = no level chosen yet — the generator falls back to the unconnected
            // height so a half-configured wall never vanishes (the column rule)
            key: 'topLevelId', label: 'Top Level', kind: 'level', default: '',
            conditionalOn: { param: 'topMode', equals: 'level' },
          },
          {
            key: 'topOffsetFt', label: 'Top Offset', kind: 'length', default: 0, min: -20, max: 20,
            conditionalOn: { param: 'topMode', equals: 'level' },
          },
          {
            // ROOF round: the wall's top DERIVES by clipping against overlapping roofs'
            // underside (bearing) planes at generation time — no stored geometry, no
            // constraint engine; detach = set back to 'none'. Positional: it encodes a
            // relationship to what's above THIS wall, so eyedropper/inject skip it
            // (derivation never auto-couples — attaching is a stated intent per wall).
            // LEGACY since the attach-targets round (2026-08-10): 'roof' still means
            // "every overlapping roof" for walls attached before per-element targets
            // existed; new attaches store explicit ids in topAttachIds instead.
            key: 'topAttach', label: 'Top Attachment', kind: 'choice', default: 'none',
            positional: true, hidden: true,
            choices: [
              { value: 'none', label: 'Unconnected height' },
              { value: 'roof', label: 'Attach to roof' },
            ],
          },
          {
            // ATTACH-TARGETS round (2026-08-10, Matt's Revit-like attach): the PICKED
            // elements (roof/floor/ceiling ids) this wall's top clips against — the
            // Revit attach model, replacing the blanket every-roof setting. Managed by
            // the pet palette's Attach top… pick flow, never a form field; positional
            // (a relationship to what's above THIS wall). Empty/null + legacy
            // topAttach:'roof' = every overlapping roof.
            key: 'topAttachIds', label: 'Top Attachment Targets', kind: 'data',
            default: null, positional: true, hidden: true,
          },
          {
            // WALL BASE ATTACH round (2026-08-10, the cheek-wall recipe): the mirror
            // of topAttachIds — the PICKED elements whose TOP surface this wall's
            // BASE clips against (a wall standing on a lower roof, or a sloped
            // floor/ceiling). No legacy mode (this shipped with picked targets from
            // day one). Managed by the pet palette's Attach base… pick flow, never a
            // form field; positional (a relationship to what's below THIS wall —
            // derivation never auto-couples). Mutually exclusive with footingEnabled
            // in v1 — a wall standing on a roof has no footing.
            key: 'baseAttachIds', label: 'Base Attachment Targets', kind: 'data',
            default: null, positional: true, hidden: true,
          },
          {
            // FOOTINGS round (2026-08-09, Matt): hidden from the schema-driven editors
            // — a "Flip Facing" toggle implies a right and a wrong way to face a wall.
            // Flipping is an ACTION now: the pet palette's Flip, or Spacebar (selected
            // wall, or live during drawing). The param itself is unchanged data.
            key: 'flipped', label: 'Flip Facing', kind: 'bool', default: false, hidden: true,
          },
          {
            // WALL JOIN round (Matt, 2026-09-21: "if walls have automatically joined
            // together, their ends should remain parametrically attached if the wall
            // they are attached to moves too... in Revit, when a wall end is joined
            // with another wall, either at a corner or anywhere along it, the end is
            // healed and the wall is automatically connected"). Deliberately a REAL
            // stored reference, not a derived-at-read-time attach (the topAttachIds
            // doctrine) — Matt's own correction: "we need to make sure the actual
            // wall properties for length are changing, not just a visual infill," so
            // this wall's own reported LENGTH (schedules, tags, everything that reads
            // the stored path) stays honest when the joined-to wall moves. `{
            // targetId, targetEnd: 'start'|'end' }` when joined, null when this end is
            // free. Auto-detected on gesture commit (never mid-drag), propagated by
            // `propagateWallJoins` (the one choke point every node/edge/rigid-move
            // command already flows through — `writePath`/`translateElement`/
            // `applyRigidTransform`), never a form field.
            key: 'startJoin', label: 'Start Join', kind: 'data', default: null, positional: true, hidden: true,
          },
          {
            key: 'endJoin', label: 'End Join', kind: 'data', default: null, positional: true, hidden: true,
          },
          {
            // Revit's "Disallow Join" — per end, since a wall may want one end free
            // to move independently while the other stays a real corner. Auto-detect
            // never creates a join on a disallowed end; an EXISTING join on that end
            // is left alone (this only blocks NEW auto-detection, matching Revit).
            key: 'startJoinDisabled', label: 'Disallow Join (Start)', kind: 'bool', default: false, hidden: true,
          },
          {
            key: 'endJoinDisabled', label: 'Disallow Join (End)', kind: 'bool', default: false, hidden: true,
          },
          {
            // GL3 ROOMS (2026-08-14, Matt's amendment): whether this wall participates
            // in room region extraction. STATED INTENT per wall — deliberately never
            // auto-derived from height (derivation never auto-couples), and positional
            // (the footingEnabled precedent: eyedropper/inject skip it — restyling a
            // wall from a pony wall must not silently unbind someone's rooms). A
            // non-bounding wall is invisible to extraction (rooms merge across it) but
            // its footprint still SUBTRACTS from a containing room's net area (the
            // ArchiCAD "Reduce Zone Area Only" rule — amendment 2).
            key: 'roomBounding', label: 'Room Bounding', kind: 'bool', default: true,
            positional: true,
          },
        ],
      },
      // WALL COPINGS (2026-10-06): this wall's cap — coping.ts
      COPING_INSTANCE_GROUP,
      {
        // TRIM round §8.12 (freeform spike — Matt: "set the bottoms and tops of walls
        // to simple profile shapes… arcs, both concave and convex"; DIRECTION FIX
        // 2026-08-20, Matt's screenshot: the arc reads in the wall FACE, spanning the
        // run's LENGTH — a circular arc segment along the centerline, identical
        // across the thickness; see bim-geometry wall/generate3d.ts#wallArcCapMesh).
        // Procedural — no profile library involved. The generator guards the
        // combinations that can't cap (attached tops, footed/stem bases, openings
        // reaching into the cap band, closed-loop walls) by falling back to flat.
        label: 'Terminations',
        params: [
          {
            key: 'topProfile', label: 'Top Profile', kind: 'choice', default: 'flat',
            choices: [
              { value: 'flat', label: 'Flat' },
              { value: 'archConvex', label: 'Arched — Convex (crowns at mid-span)' },
              { value: 'archConcave', label: 'Arched — Concave (dips at mid-span)' },
            ],
          },
          {
            key: 'topProfileRiseIn', label: 'Top Arc Rise (in)', kind: 'number', default: 12, min: 0.25, max: 48,
            conditionalOn: { param: 'topProfile', equals: ['archConvex', 'archConcave'] },
          },
          {
            key: 'bottomProfile', label: 'Bottom Profile', kind: 'choice', default: 'flat',
            choices: [
              { value: 'flat', label: 'Flat' },
              { value: 'archConvex', label: 'Arched — Convex (hangs at mid-span)' },
              { value: 'archConcave', label: 'Arched — Concave (arched underside, opens at mid-span)' },
            ],
          },
          {
            key: 'bottomProfileRiseIn', label: 'Bottom Arc Rise (in)', kind: 'number', default: 12, min: 0.25, max: 48,
            conditionalOn: { param: 'bottomProfile', equals: ['archConvex', 'archConcave'] },
          },
        ],
      },
      {
        // CANTED WALLS round (freeform Phase 2 — docs/bim-plan/15, Matt's resolved
        // §7): battered faces, specified per face as a TOP INSET in inches (the
        // dimension a section carries; the angle derives, clamped to 30° from
        // plumb). Mutually exclusive with footing/stem, top/base attach, and the
        // §8.12 arc terminations — the kernel predicate (wallCant.ts
        // #isCantedWallParams) owns the exclusions, and a wall that trips one
        // silently builds plumb. Openings work (flat-headed; the piecewise-sweep
        // void model); corners between SEPARATE canted walls butt (draw a battered
        // mass as one multi-segment element for the mitered arris).
        label: 'Cant (Battered Faces)',
        params: [
          {
            key: 'cantExtInsetIn', label: 'Exterior Face Top Inset (in)', kind: 'number',
            default: 0, min: 0, max: 48,
          },
          {
            key: 'cantIntInsetIn', label: 'Interior Face Top Inset (in)', kind: 'number',
            default: 0, min: 0, max: 48,
          },
          {
            // LEAN (Matt's follow-up, same day): the WHOLE wall tips — both faces
            // shift the same top offset, thickness constant. Signed: + tips the top
            // toward the INTERIOR side, − toward the exterior. Composes with the
            // per-face insets (a leaning battered wall is both at once). Unlike the
            // insets it consumes no core, so only the 30° clamp applies.
            key: 'cantLeanIn', label: 'Lean (in, + toward interior)', kind: 'number',
            default: 0, min: -48, max: 48,
          },
        ],
      },
      {
        // WALL FRAMING round (the E2 pattern applied to walls) — the derived-framing
        // STRUCTURE standard. Stud SIZE lives on the TYPE's core definition now
        // (WALL CORE DEFINITION round — the assembly's core thickness derives from
        // the selected size and IS the stud depth); headers size from the curated
        // span→member rule table (kernel wallFraming.ts).
        // INSTANCE, not type (2026-08-18, Matt): every wall of a given type doesn't
        // necessarily want the same field-stud module — a pure instance param, no
        // type default to shadow (the casework WIDTH precedent), defaulting to 16" o.c.
        // Both params gate on a STUD core (cross-schema conditionalOn — the
        // hand-on-operation precedent): CMU/concrete cores have no stud module and
        // no cavity to insulate.
        label: 'Framing',
        params: [
          {
            key: 'studSpacingIn', label: 'Stud Spacing', kind: 'choice', default: '16',
            conditionalOn: { param: 'coreType', equals: ['wood', 'metal'] },
            choices: STUD_SPACINGS.map((s) => ({ value: String(s), label: `${s}″ o.c.` })),
          },
          {
            // WALL CORE DEFINITION round (2026-08-18, Matt): insulation is a per-WALL
            // fact, not a material — toggling it on draws the batt hatch over the
            // core band in plan and section (plan.ts/section.ts read
            // el.params.insulation against the band's isCore flag). No 3D effect.
            key: 'insulation', label: 'Insulation', kind: 'bool', default: false,
            conditionalOn: { param: 'coreType', equals: ['wood', 'metal'] },
          },
          {
            // SHEATHING (2026-10-14, Matt: "since it is possible for an exterior wall to NOT have
            // sheathing, I am wondering if we need to turn that into a toggleable feature for
            // exterior walls"): off = this wall leaves out its type's exterior sheathing (an
            // older house with siding straight on the studs). The studs never move (the
            // painted-finish shift absorbs it). On by default, so every wall reads as before.
            // A new exterior finish troweled on in a later phase brings new sheathing with it
            // (wallScopeAfter.ts).
            key: 'sheathing', label: 'Sheathing', kind: 'bool', default: true,
            conditionalOn: { param: 'function', equals: ['exterior'] },
          },
          // PAINTED WALL FINISHES (2026-09-28): the finish painted on each face with
          // the Paint Bucket (a material id) — becomes a real assembly layer at the
          // material's thickness (wallCladding.ts)
          { key: 'finishExterior', label: 'Exterior Finish', kind: 'data', default: null, hidden: true },
          { key: 'finishInterior', label: 'Interior Finish', kind: 'data', default: null, hidden: true },
          // COLORS (2026-10-05): the color brushed on each face over its finish (colors.ts id)
          { key: 'colorExterior', label: 'Exterior Color', kind: 'data', default: null, hidden: true },
          { key: 'colorInterior', label: 'Interior Color', kind: 'data', default: null, hidden: true },
          // WALL END FINISHES (2026-10-06, Matt: "apply material finishes to the ends of walls
          // where they don't join to anything"): the finish wrapping each FREE end (a material
          // id) — built as a real layer at its thickness, the wall growing outward there
          { key: 'finishStart', label: 'Start End Finish', kind: 'data', default: null, hidden: true },
          { key: 'finishEnd', label: 'End Finish', kind: 'data', default: null, hidden: true },
          // WALL FINISH SPLITS (2026-10-02): level split lines across a face, each
          // carrying the finish of the band above it, plus the band under the lowest
          // line (wallFaceSplits.ts). Face finishes: thickness stays the layer's.
          { key: 'faceSplits', label: 'Finish Splits', kind: 'data', default: null, hidden: true },
          { key: 'faceBaseExterior', label: 'Exterior Base Band', kind: 'data', default: null, hidden: true },
          { key: 'faceBaseInterior', label: 'Interior Base Band', kind: 'data', default: null, hidden: true },
          // WALL BANDS (2026-10-04): profiles run along a face — a row-lock course, …
          // (wallSweeps.ts)
          { key: 'faceSweeps', label: 'Wall Bands', kind: 'data', default: null, hidden: true },
          // SCOPE ON THE ELEMENT (scope.ts): what the remodel does to each layer of this wall
          { key: 'scope', label: 'Scope', kind: 'data', default: null, hidden: true },
          // FINISH-FREE TYPES: the centerline correction (feet) that held this wall's
          // studs in place when it moved off a finished type (finishStripMigration.ts)
          { key: 'finishAnchorFt', label: 'Finish Anchor', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        // FOOTINGS round (docs/MODELER.md "WALL FOOTINGS + STEM WALLS"): per-wall
        // foundation, fully derived — nothing stored but intent. The toggle is
        // positional (the topAttach precedent: a relationship to what's below THIS
        // wall — derivation never auto-couples, footing intent is stated per wall,
        // so eyedropper/inject skip it); the type refs and mode transfer as styling.
        label: 'Foundation',
        params: [
          {
            key: 'footingEnabled', label: 'Footing + Stem Wall', kind: 'bool', default: false,
            positional: true,
          },
          {
            key: 'footingTypeId', label: 'Footing Type', kind: 'typeRef', refCategory: 'footing',
            default: '', conditionalOn: { param: 'footingEnabled', equals: true },
          },
          {
            // WALL-IS-STEM (2026-08-10, Matt): a CMU/concrete wall with no applied
            // finishes IS its own stem — no stem prisms are emitted and the wall
            // body extends down to the SAME footing an independent stem would have
            // produced (the stem type below still governs the footing's alignment
            // and the vertical layout, it just stops being built). The curb rule is
            // moot — nothing wood needs lifting above grade.
            key: 'wallIsStem', label: 'Wall Is the Stem', kind: 'bool', default: false,
            conditionalOn: { param: 'footingEnabled', equals: true },
          },
          {
            key: 'stemTypeId', label: 'Stem Wall Type', kind: 'typeRef', refCategory: 'stem',
            default: '', conditionalOn: { param: 'footingEnabled', equals: true },
          },
          {
            // 'manual': stemHeightFt is the extension DOWN from the wall's base to the
            // TOP of the footing. 'topo': the height derives from the topographic
            // surface along the run via the footing type's coverage rules, stepping at
            // its increment as grade falls — re-derived on every generate.
            key: 'stemMode', label: 'Stem Height By', kind: 'choice', default: 'manual',
            conditionalOn: { param: 'footingEnabled', equals: true },
            choices: [
              { value: 'manual', label: 'Manual height' },
              { value: 'topo', label: 'Topography coverage' },
            ],
          },
          {
            key: 'stemHeightFt', label: 'Stem Height', kind: 'length', default: 2, min: 0, max: 30,
            conditionalOn: [
              { param: 'footingEnabled', equals: true },
              { param: 'stemMode', equals: 'manual' },
            ],
          },
          {
            // Coverage OVERRIDE (nullable — the roof-overhang shadow pattern): null =
            // the PROJECT structural setting (frost depth is a site condition, set
            // once in the Structural… modal). Overriding per wall covers the odd
            // localized condition without touching the project standard.
            key: 'coverageDepthFt', label: 'Coverage Depth', kind: 'length',
            default: null, optional: true, optionalHint: 'project standard', min: 0, max: 10,
            conditionalOn: [
              { param: 'footingEnabled', equals: true },
              { param: 'stemMode', equals: 'topo' },
            ],
          },
          {
            // CURB round (2026-08-10, Matt): where the wall BASE runs below finish
            // grade, the stem steps UP past the wall base to at least this height
            // ABOVE grade — the wall lands on a non-subterranean sill plate. Grade is
            // sampled on BOTH sides of the wall and the HIGH side governs (a retained
            // wall's curb rises above its high side; along an unretained slope both
            // sides agree). 0 disables (the pre-round behavior). Steps quantize on
            // the same project-datum grid as the footing steps.
            key: 'curbHeightFt', label: 'Min Curb Above Grade', kind: 'length',
            default: 0.5, min: 0, max: 5,
            conditionalOn: [
              { param: 'footingEnabled', equals: true },
              { param: 'stemMode', equals: 'topo' },
              { param: 'wallIsStem', equals: false },
            ],
          },
          {
            // FRAMED CRAWL SPACE round (2026-08-16, Matt): governs the extension-
            // DOWN condition only — stations where the wall's base level sits
            // ABOVE the terrain. OFF (default): the stem runs FULL HEIGHT up to
            // the wall base (a framed floor bearing on it derives the mudsill
            // seat as usual) — one tall concrete wall; only the FOOTING steps
            // with grade. ON: the concrete is minimized — the stem top steps
            // DOWN following grade + the minimum curb, and the crawl space
            // between stem and floor framing is FRAMED (the wall body + derived
            // framing extend down to the stepped stem; platform framing breaks
            // at the floor framing as in every other platform condition).
            // Stations where grade rises ABOVE the base (buried base) stay in
            // the true curb regime regardless — the curb is about lifting wood
            // above grade, and this toggle never applies there.
            key: 'framedCrawlSpace', label: 'Framed Crawl Space', kind: 'bool', default: false,
            conditionalOn: [
              { param: 'footingEnabled', equals: true },
              { param: 'stemMode', equals: 'topo' },
              { param: 'wallIsStem', equals: false },
            ],
          },
          {
            // FINISH SKIRT round (2026-08-16, Matt): by DEFAULT the exterior
            // finish + sheathing run continuously across the floor band down to
            // the top of concrete (no setting — it's the standard condition).
            // This option continues them (the sheathing riding along as furring)
            // DOWN OVER the exposed concrete to a 4" clearance above finish
            // grade, the bottom edge running at an angle with the grade line —
            // the weep-screed read. Applies where the concrete is exposed above
            // grade; buried stretches keep the default termination.
            key: 'finishToGrade', label: 'Finish Down To Grade', kind: 'bool', default: false,
            conditionalOn: [
              { param: 'footingEnabled', equals: true },
              { param: 'wallIsStem', equals: false },
            ],
          },
          {
            // FINISH SKIRT OPT-OUT (2026-08-18, Matt): buildFinishSkirt() (bim-geometry
            // generate3d.ts) auto-extends the exterior finish + sheathing down over an
            // adjoining floor's rim-joist band whenever this wall's own base Z lands
            // within tolerance of that floor's sheathing top — no attach relationship
            // required, "bearing intent by modeled overlap" (the doctrine above). That's
            // the intended default with no toggle, but a wall can coincidentally sit near
            // a floor band it has no real bearing relationship to. This lets one specific
            // wall opt all the way out. Positional (a stated intent about THIS wall, not
            // styling — eyedropper/inject skip it, the footingEnabled precedent) and NOT
            // gated on footingEnabled — the auto-skirt fires in both the footed and
            // non-footed branches.
            key: 'noFinishSkirt', label: 'Disable Auto Finish Skirt', kind: 'bool',
            default: false, positional: true,
          },
          {
            // TERRAIN DECOUPLE round (docs/bim-plan/19): the CAPTURED terrain
            // profile — see TerrainFit in types.ts for the full doctrine note.
            // Hidden (managed by the "Update to terrain" action, never a form
            // field) and positional (a fit to the terrain under THIS wall —
            // eyedropper/inject transferring it would stamp one wall's site
            // condition onto another). Absent = live derivation (pre-round
            // behavior). Cleared when footingEnabled turns off (applyParamEdit
            // routing — dead data otherwise, and re-enabling should re-capture
            // fresh).
            key: 'terrainFit', label: 'Terrain Fit', kind: 'data',
            default: null, positional: true, hidden: true,
          },
        ],
      },
      // ATMOSPHERE VIEW Stage 3: per-element texture shift/rotate (texturePlacement.ts)
      TEXTURE_PLACEMENT_GROUP,
    ],
  },
}
